import { Injectable, OnDestroy } from '@angular/core';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { of, timer } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';

import { AuthState, AuthStatus } from '../../../auth/auth.model';
import { AuthService } from '../../../auth/auth.service';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../../common/take-until-destroy.decorator';
import {
    Category,
    CategoryAction,
    EventCentreHook,
    EventCentreHookService,
    Item,
    ItemAction,
} from '../core';
import { EventCentreDialogService } from '../dialog';

import { EventCentreCPopDialogComponent } from './event-centre-c-pop-dialog.component';
import { CPopMapItem } from './model/c-pop-map-item.model';
import { CPopTemplateV1 } from './model/c-pop-template-v1.model';
import { CPopTemplateV2 } from './model/c-pop-template-v2.model';
import { CPopTemplateVersion } from './model/c-pop-template-version.model';

@Injectable({ providedIn: 'root' })
@TakeUntilDestroy()
export class CPopEventCentreHookService implements OnDestroy, EventCentreHook {
    private _dialogRefs: Array<CmpsDialogRef<unknown, unknown>>;

    constructor(
        private readonly _hookService: EventCentreHookService,
        private readonly _dialogService: EventCentreDialogService,
        private readonly _authService: AuthService,
        private readonly _dialog: CmpsDialog,
    ) {
        this._dialogRefs = [];

        this._bindAuth();

        this._hookService.register(this);
    }

    ngOnDestroy(): void {
        this._hookService.unregister(this);

        this._dialogRefs = [];
    }

    ecOnActionInvoked(item: Item, action: ItemAction): boolean {
        return false;
    }

    ecOnItemsChanged(items: Item[]): void {
        const cPopTpls = this._determineCPopTpls(items);
        const unread = cPopTpls.filter((e) => !e.item.read);
        if (unread.length > 0) {
            this._openDialog(unread, 2000);
        }
    }

    ecOnCategoryActionInvoked(
        category: Category,
        action: CategoryAction,
        items: Item[],
    ): void {
        if (action.command?.toLowerCase()?.trim() !== 'c-pop') {
            return;
        }
        const cPopTpls = this._determineCPopTpls(items);
        if (cPopTpls.length > 0) {
            this._openDialog(cPopTpls, 0);
        }
    }

    private _bindAuth(): void {
        this._authService.authStatus
            .pipe(
                tap((authStatus) => {
                    if (
                        [
                            AuthState.LOGOUT,
                            AuthState.TIMEOUT,
                            AuthState.LOST,
                        ].includes(this._authService.authState) &&
                        authStatus !== AuthStatus.AUTHENTICATED
                    ) {
                        const dialogRefs = [...this._dialogRefs];
                        dialogRefs.forEach((e) => e.close());
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _determineCPopTpls(items: Item[]): CPopMapItem[] {
        const cPopTpls: CPopMapItem[] = [];
        items.forEach((item) => {
            item.extensions?.forEach((e) => {
                try {
                    const payload = e?.split('|', 2);
                    if (payload?.[0]?.toLowerCase()?.trim() !== 'c-pop') {
                        return;
                    }
                    const obj = JSON.parse(payload?.[1]);
                    const [canHandle, tpl] = this._canHandle(obj);
                    if (!canHandle) {
                        return;
                    }
                    tpl.elements?.sort(
                        (a, b) =>
                            (a.displayOrder ?? Number.MAX_SAFE_INTEGER) -
                            (b.displayOrder ?? Number.MAX_SAFE_INTEGER),
                    );
                    cPopTpls.push({ item, tpl });
                } catch {
                    // nothing
                }
            });
        });
        return cPopTpls;
    }

    private _canHandle(obj: any): [boolean, CPopTemplateV1 | CPopTemplateV2] {
        const base: CPopTemplateVersion = obj;
        if (base.version?.trim()?.toLowerCase() === '1.0.0') {
            const tpl: CPopTemplateV1 = obj;
            return [true, tpl];
        }
        if (base.version?.trim()?.toLowerCase() === '2.0.0') {
            const tpl: CPopTemplateV2 = obj;
            return [true, tpl];
        }
    }

    private _openDialog(cPopTpls: CPopMapItem[], delay: number): void {
        let ref: CmpsDialogRef<unknown, unknown> | null;
        (delay === 0 ? of(null) : timer(delay))
            .pipe(
                filter(
                    () =>
                        this._authService.authStatus.value ===
                        AuthStatus.AUTHENTICATED,
                ),
                switchMap(() => {
                    ref = this._dialog.open(EventCentreCPopDialogComponent, {
                        data: cPopTpls,
                    });
                    this._dialogRefs.push(ref);
                    return ref.result;
                }),
                tap(() => {
                    const index = this._dialogRefs.indexOf(ref);
                    if (index !== -1) {
                        this._dialogRefs.splice(index, 1);
                    }
                }),
                filter(
                    () =>
                        this._authService.authStatus.value ===
                        AuthStatus.AUTHENTICATED,
                ),
                tap(() => {
                    cPopTpls.forEach((e) => {
                        this._dialogService.updateItem(
                            e.item,
                            true,
                            e.item.deleted,
                        );
                    });
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
